import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { destinationResortFilterOptions, isServerSide, sortOptions } from "app/utils/utils";
import {
	defaultValues,
	validateDestination,
	validateSearch,
} from "app/pages/SmartDP/Search/SDPSearchForm/smartDPSearchFormSchema";
import { DESTINATION_USA_CODE, FS_QUOTE_EVENT_NAME, WEBSITE_TUNNEL_TYPES } from "app/constants";
import { FormattedMessage } from "react-intl";
import { Form, Formik } from "formik";
import SmartDPSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchInput";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import ClearIndicatorSdpSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/ClearIndicatorSdpSearchInput";
import get from "lodash/get";
import IconLocation from "app/pages/.shared/IconLocation";
import DestinationInputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/DestinationInputValueFormat";
import CalendarDisabledView from "app/pages/SmartDP/Search/SDPSearchForm/CalendarDisabledView";
import { differenceInDays } from "date-fns";
import TravellersRoomDrawerButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomDrawerButton";
import TravellersRoomButton from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomButton";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import PropTypes from "prop-types";
import HotelOnlyDateCalendarContainer from "app/pages/HotelOnly/HotelOnlySearchForm/HotelOnlyDateCalendarContainer";
import "app/pages/.shared/form/SearchForm.scss";
import "./HotelOnlySearchForm.scss";
import LastSearchContainer from "app/features/SearchEngine/LastSearch/LastSearchContainer";
import { debounce } from "lodash";

const DESTINATION_MAX_RESULTS_TO_DISPLAY = 50;

const HotelOnlySearchForm = ({
	onSuccess = () => {},
	destinations = [],
	initialValues = {},
	validateOnMount,
	displayLoader,
	errorScrollIntoView = true,
	onFieldFocus = () => {},
	isAutoScrollActive,
	resetAllSDPProductsFilter = () => {},
}) => {
	const { sendHits } = useFlagship();

	const selectRefs = {
		destinationResort: useRef(null),
		departureDate: useRef(null),
	};
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const handleSubmit = useCallback(
		(values, actions) => {
			resetAllSDPProductsFilter();
			actions.setSubmitting(false);
			onSuccess({ values, pathname });
		},
		[destinations, pathname]
	);
	const [destinationInput, setDestinationInput] = useState();
	// fix pour ne pas avoir les labels sur 2 lignes au premier rendu
	const mergeInitialValues = isServerSide
		? {
				occupancies: [{ adults: 2, children: 0, childrenBirthdates: [] }],
		  }
		: {
				...defaultValues,
				...initialValues,
		  };

	const isListingOrQuote =
		pathname.includes("/hotelonly/listing") || pathname === "/hotelonly/booking/quote";

	const onHandleClickSearchButton = useCallback(() => {
		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_QUOTE_EVENT_NAME.CLICK_ON_SEARCH_CTA,
		});
	}, []);

	const [refs, setRefs] = useState(null);

	const updateFloatingPositionReference = useCallback(refs => {
		setRefs(refs);
	}, []);
	const customFieldIds = ["departureDate"];

	const fieldIds = useMemo(() => ["destinationResort", "departureDate"], []);

	const fieldOrder = useMemo(() => fieldIds.map(id => ({ id, ref: selectRefs[id] })), [
		fieldIds,
		selectRefs,
	]);
	const getFieldValue = (values, fieldId) => {
		const nestedFields = {
			departureDate: ["travelDates", "departureDate"],
		};

		if (nestedFields[fieldId]) {
			return nestedFields[fieldId].reduce(
				(acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
				values
			);
		}

		return values[fieldId];
	};

	const handleFieldChange = useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (value) {
				const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
				if (currentIndex === -1) {
					return;
				}

				const previousFields = fieldOrder.slice(0, currentIndex);
				const firstEmptyPrevField = previousFields.find(
					field => !getFieldValue(values, field.id)
				);

				if (firstEmptyPrevField && firstEmptyPrevField.ref.current) {
					if (customFieldIds.includes(firstEmptyPrevField.id)) {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.click();
						});
					} else {
						requestAnimationFrame(() => {
							firstEmptyPrevField.ref.current.focus();
						});
					}
					return;
				}

				let nextIndex = currentIndex + 1;

				while (nextIndex < fieldOrder.length) {
					const nextField = fieldOrder[nextIndex];
					if (!getFieldValue(values, nextField.id) && nextField.ref.current) {
						requestAnimationFrame(() => {
							nextField.ref.current.click();
						});
						break;
					}
					nextIndex++;
				}
			}
		},
		[fieldOrder, customFieldIds]
	);

	const [filteredDestinations, setFilteredDestinations] = useState([]);

	const debouncedFilter = useCallback(
		debounce(input => {
			if (input) {
				const filtered = sortOptions(destinations, input).slice(
					0,
					DESTINATION_MAX_RESULTS_TO_DISPLAY
				);
				setFilteredDestinations(filtered);
			}
		}, 300),
		[destinations]
	);

	return (
		<div className="search-form search-form--hotel-only" ref={refs?.setPositionReference}>
			<Formik
				enableReinitialize
				initialValues={mergeInitialValues}
				validate={validateSearch(true)}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={
					validateOnMount && {
						destinationResort: true,
						travelDates: {
							departureDate: true,
							endDate: true,
						},
						duration: {
							code: true,
						},
						occupancies: true,
					}
				}
				validateOnMount={validateOnMount}
			>
				{({ values, errors, isValid, isSubmitting, setFieldValue, submitCount }) => {
					const handleTravellersConfirmation = values => {
						setFieldValue("occupancies", values);
					};

					const isUsaDestination = DESTINATION_USA_CODE.includes(
						values?.destinationResort?.code
					);

					return (
						<>
							<Form
								className="search-form__form search-form--hotel-only"
								data-testid="sdp-search-form"
							>
								<SmartDPSearchInput
									menuIsOpen={Boolean(destinationInput)}
									id="destinationResort"
									name="destinationResort"
									selectRef={selectRefs.destinationResort}
									openMenuOnFocus={!values.destinationResort}
									openMenuOnClick={!values.destinationResort}
									validate={validateDestination}
									onChange={value =>
										handleFieldChange(
											"destinationResort",
											setFieldValue,
											value,
											values
										)
									}
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field search-form__field-destination"
									data-testid="destination-resort-input"
									filterOption={destinationResortFilterOptions}
									label={
										<FormattedMessage id="hotel.only.search.destination.input.label" />
									}
									drawerInputLabel={
										<FormattedMessage id="hotel.only.search.destination.input.label" />
									}
									formatOptionLabel={({ labels = [] }) => {
										return (
											<div className="search-form__suggestion">
												<div className="search-form__suggestion-picto">
													<IconLocation />
												</div>
												<div className="search-form__suggestion-text">
													<span
														className="search-form__suggestion-first-label"
														dangerouslySetInnerHTML={{
															__html: labels?.slice(-1).join(", "),
														}}
													/>
													<span
														className="search-form__suggestion-second-label"
														dangerouslySetInnerHTML={{
															__html: labels?.slice(0, -1).join(", "),
														}}
													/>
												</div>
											</div>
										);
									}}
									components={{
										SingleValue: DestinationInputValueFormat,
										ClearIndicator: ClearIndicatorSdpSearchInput,
										MenuList: LocationMenuList,
									}}
									getOptionValue={({ code }) => code}
									getOptionLabel={({ label }) => label}
									options={filteredDestinations}
									// on a ajouté  {|| "" } dans value de l'input pour forcer l'initialisation du champ s'il reçoit rien comme
									// valeur par defaut
									value={
										destinations.find(({ code }) => {
											return (
												code ===
												Number(get(values, "destinationResort.code"))
											);
										}) || ""
									}
									noOptionsMessage={() => {
										return <FormattedMessage id="sdp.search.no.result.label" />;
									}}
									popperWidth={360}
									popperOffset={[-17, 2]}
									locationMenuLabel={
										<FormattedMessage id="sdp.search.top.destinations.cities.label" />
									}
									isTopLocationMenuDisplayed={false}
									onInputChange={inputValue => {
										setDestinationInput(inputValue);
										debouncedFilter(inputValue);
									}}
									loading={displayLoader}
									isFloating
									floatingConfig={{
										placement: "bottom-start",
										offset: ({ rects }) => ({
											mainAxis: -rects.reference.height - 16,
											crossAxis: -16,
										}),
									}}
									placeholder={
										<FormattedMessage id="sdp.search.destination.input.label.mobile" />
									}
									showPlaceholderOnlyOnFocus={true}
								/>

								<HotelOnlyDateCalendarContainer
									id="travelDates"
									name="travelDates"
									data-testid="travel-date-input"
									selectRef={selectRefs.departureDate}
									onFocus={onFieldFocus}
									isAutoScrollActive={isAutoScrollActive}
									className="search-form__field"
									popperOffset={[0, -58]}
									updateFloatingPositionReference={
										updateFloatingPositionReference
									}
									calendarPopoverWidth={1024}
									departureLabel={
										<FormattedMessage id="hotel.only.search.departure.date.side.panel.title" />
									}
									returnLabel={
										<FormattedMessage id="hotel.only.search.destination.date.side.panel.title" />
									}
									calendarDisabledView={
										<CalendarDisabledView
											mainMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="hotel.only.calendar.unavailable.date.label" />
												) : (
													<FormattedMessage id="hotel.only.calendar.no.destination.no.departure.city.label" />
												)
											}
											secondaryMessage={
												values.departureCity && values.destinationResort ? (
													<FormattedMessage id="sdp.calendar.unavailable.date.sublabel" />
												) : null
											}
										/>
									}
									handleDayChange={({
										selectedEndDate,
										selectedDepartureDate,
									}) => {
										if (selectedEndDate && selectedDepartureDate) {
											const daysDifference = differenceInDays(
												new Date(selectedEndDate),
												new Date(selectedDepartureDate)
											);
											setFieldValue("duration", {
												code: daysDifference,
											});
										}
									}}
									loading={displayLoader}
								/>
								{isMobile ? (
									<TravellersRoomDrawerButton
										className="search-form__field search-form__field-rooms search-form__field--fullsize search-form__field-travellers-hotel-only"
										id="travellersRooms"
										onConfirmation={handleTravellersConfirmation}
										initialValues={values.occupancies}
										label={
											<FormattedMessage id="sdp.search.travellers.input.label" />
										}
										validateOnMount={validateOnMount}
										warningMessage={isUsaDestination}
									/>
								) : (
									<TravellersRoomButton
										className="search-form__field search-form__field-rooms search-form__field--fullsize"
										id="travellersRooms"
										onFocus={onFieldFocus}
										isAutoScrollActive={isAutoScrollActive}
										onConfirmation={handleTravellersConfirmation}
										initialValues={values.occupancies}
										label={
											<FormattedMessage id="sdp.search.travellers.input.label" />
										}
										stickyMode={false}
										validateOnMount={validateOnMount}
										loading={displayLoader}
										popperWidth={360}
										popperOffset={[80, 4]}
										warningMessage={isUsaDestination}
									/>
								)}

								{/* Do not display error message on validationOnMount (redirection from emirates) */}
								{!isValid && submitCount >= 1 && (
									<div className="search-form__error">
										<FormErrorMessages
											errors={errors}
											isSubmitting={isSubmitting}
											submitCount={submitCount}
											disableErrorsDetails={true}
											message={
												<FormattedMessage id="sdp.search.form.error.message" />
											}
											scrollInToView={errorScrollIntoView}
										/>
									</div>
								)}
								<div className="search-form__footer">
									<Button
										variant={BUTTON_VARIANTS.FILLED}
										size={BUTTON_SIZES.LARGE}
										label={
											<FormattedMessage
												id={
													isListingOrQuote
														? "sdp.search.refresh.label"
														: "sdp.search.cta.label"
												}
											/>
										}
										submit
										className="search-form__button"
										loading={isSubmitting}
										data-testid="search-form-button"
										onClick={onHandleClickSearchButton}
										disabled={displayLoader || isSubmitting}
									/>

									<div className="search-form__last-search">
										<LastSearchContainer
											websiteType={WEBSITE_TUNNEL_TYPES.HOTEL_ONLY}
											updateFloatingPositionReference={
												updateFloatingPositionReference
											}
											onFocus={onFieldFocus}
										/>
									</div>
								</div>
								<div className="search-form__first-line-break" />
							</Form>
						</>
					);
				}}
			</Formik>
		</div>
	);
};

HotelOnlySearchForm.propTypes = {
	onSuccess: PropTypes.func,
	destinations: PropTypes.array,
	initialValues: PropTypes.object,
	onFieldFocus: PropTypes.func,
	validateOnMount: PropTypes.bool,
	displayLoader: PropTypes.bool,
	errorScrollIntoView: PropTypes.bool,
	isAutoScrollActive: PropTypes.bool,
	resetAllSDPProductsFilter: PropTypes.func,
};

export default memo(HotelOnlySearchForm);
